import React, { useState, useEffect } from 'react';
import { Input, List, Badge, Typography } from 'antd';
import { ClockCircleOutlined, CloseOutlined } from '@ant-design/icons';
import { SelectedItemsContainer, SelectedItemsWrap, DropdownContainer, SuggestionsDropdown, SectionTitle, SecondaryText, StyledTag, IconSpacer, ScheduleTimeText, ListItemContent, ListItemSelected } from '../styles';
import type { SchedulesSelectorProps } from '../types';
import { PLACEHOLDERS, MESSAGES } from '../constants';

const SchedulesSelector: React.FC<SchedulesSelectorProps> = ({
  schedules,
  selectedSchedules,
  onScheduleChange,
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [query, setQuery] = useState('');
  const [filteredSchedules, setFilteredSchedules] = useState(schedules);

  useEffect(() => {
    setFilteredSchedules(schedules);
  }, [schedules]);

  const handleSearchSchedules = (value: string) => {
    setQuery(value);
    setFilteredSchedules(
      schedules.filter((schedule) =>
        schedule.day_names.join(', ').toLowerCase().includes(value.toLowerCase())
      )
    );
  };

  const handleClearSearch = () => {
    setQuery('');
    setFilteredSchedules(schedules);
  };

  const handleSelectSchedule = (schedule: typeof schedules[0]) => {
    const isAlreadySelected = selectedSchedules.some((sch) => sch.id === schedule.id);

    if (!isAlreadySelected) {
      onScheduleChange([...selectedSchedules, schedule]);
    }

    setShowSuggestions(false);
    setQuery('');
  };

  const handleRemoveSchedule = (scheduleId: string) => {
    const updated = selectedSchedules.filter((sch) => sch.id !== scheduleId);
    onScheduleChange(updated);
  };

  return (
    <div>
      <SectionTitle level={5}>Schedules</SectionTitle>
      <DropdownContainer>
        <Input
          placeholder={PLACEHOLDERS.SELECT_SCHEDULES}
          value={query}
          onChange={(e) => handleSearchSchedules(e.target.value)}
          onFocus={() => setShowSuggestions(true)}
          suffix={query ? <CloseOutlined onClick={handleClearSearch} style={{ cursor: 'pointer' }} /> : null}
        />

        {showSuggestions && filteredSchedules.length > 0 && (
          <SuggestionsDropdown>
            <List
              dataSource={filteredSchedules}
              renderItem={(schedule) => {
                const isSelected = selectedSchedules.some((sch) => sch.id === schedule.id);
                return (
                  <ListItemSelected
                    $isSelected={isSelected}
                    onClick={() => handleSelectSchedule(schedule)}
                  >
                    <ListItemContent direction="vertical" size={4}>
                      <div>{schedule.day_names.join(', ')}</div>
                      <ScheduleTimeText>
                        <ClockCircleOutlined />
                        <IconSpacer />
                        {schedule.start_time} - {schedule.end_time}
                      </ScheduleTimeText>
                    </ListItemContent>
                    {isSelected && (
                      <Badge status="success" text="Selected" />
                    )}
                  </ListItemSelected>
                );
              }}
            />
          </SuggestionsDropdown>
        )}
      </DropdownContainer>

      <SelectedItemsContainer>
        <Typography.Text strong>Selected Schedules:</Typography.Text>
        {selectedSchedules.length === 0 ? (
          <SecondaryText type="secondary">
            {MESSAGES.NO_SCHEDULES}
          </SecondaryText>
        ) : (
          <SelectedItemsWrap>
            {selectedSchedules.map((schedule) => (
              <StyledTag
                key={schedule.id}
                closable
                onClose={() => handleRemoveSchedule(schedule.id)}
                color="blue"
              >
                {schedule.day_names.join(', ')}
              </StyledTag>
            ))}
          </SelectedItemsWrap>
        )}
      </SelectedItemsContainer>
    </div>
  );
};

export default React.memo(SchedulesSelector);
